import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import LocationIcon from "../../../icons/LocationIcon";
import TimerGreyIcon from "../../../icons/TimerGreyIcon";
import UploadIcon from "../../../icons/UploadIcon";
import FileUploadWhiteIcon from "../../../icons/FileUploadWhiteIcon";
import { MileageEntry } from "../../../constants/types";
import "./style.scss";
import { recurringDays } from "../../../constants/utlis";
import DeleteModal from "../../../components/deleteModal";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { MAX_FILE_SIZE, MAX_FILES, MAX_TOTAL_SIZE } from "../../../constants/common";
import { toast } from "react-toastify";

const libraries: "places"[] = ["places"];

interface MileageEntryModalProps {
  show: boolean;
  onHide: () => void;
  data?: MileageEntry | null;
  onSave: (entry: MileageEntry) => void;
}

const MileageEntryModal: React.FC<MileageEntryModalProps> = ({
  show,
  onHide,
  data,
  onSave,
}) => {
  // Google Maps Loader
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  const [startingLocation, setStartingLocation] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [sourceCoordinates, setSourceCoordinates] = useState("");
  const [destinationCoordinates, setDestinationCoordinates] = useState("");
  const [route_trip, setRouteTrip] = useState(false);
  const [miles, setMiles] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [recurringError, setRecurringError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImageUuid, setSelectedImageUuid] = useState<string | null>(
    null
  );
  const [existingMileageEntry, setExistingMileageEntry] = useState("");

  const [startAutocomplete, setStartAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [endAutocomplete, setEndAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [directionsResponse, setDirectionsResponse] =
    useState<google.maps.DirectionsResult | null>(null);


  useEffect(() => {
    if (!show) {
      setRecurringError("");
      setSelectedDays([]);
      setFiles([]);
      setExistingImages([]);
      setStartingLocation("");
      setDestinationLocation("");
      setSourceCoordinates("");
      setDestinationCoordinates("");
      setRouteTrip(false);
      setMiles("");
      setIsRecurring(false);
      setExistingMileageEntry("");
      setDirectionsResponse(null);
    }
  }, [show]);

  // Pre-fill form if editing
  useEffect(() => {
    if (data && show) {
      setStartingLocation(data.startingLocation || "");
      setDestinationLocation(data.destinationLocation || "");
      setRouteTrip(data.route_trip || false);
      setMiles(String(data.miles || ""));
      setIsRecurring(data.isRecurring || false);
      const days =
        data.days === "everyday"
          ? recurringDays
          : data.days
            ? data.days.split(",").map((day) => day.trim().toLowerCase())
            : [];
      setSelectedDays(days);
      setFiles(data.photos || []);
      setExistingImages(data.receipts || []);
      setExistingMileageEntry(data.uuid || "");
    }
  }, [data, show]);

  // Helper: Convert LatLng to "lat, lng"
  const latLngToString = (loc: google.maps.LatLng | undefined) => {
    if (!loc) return "";
    return `${loc.lat().toFixed(6)}, ${loc.lng().toFixed(6)}`;
  };

  // Handle place selection + calculate route
  const handlePlaceChange = (type: "start" | "end") => {
    if (!isLoaded || !window.google) return;

    let startPlace = startAutocomplete?.getPlace();
    let endPlace = endAutocomplete?.getPlace();

    // Update address
    if (type === "start" && startPlace?.formatted_address) {
      setStartingLocation(startPlace.formatted_address);
      if (startPlace.geometry?.location) {
        setSourceCoordinates(latLngToString(startPlace.geometry.location));
      }
    }

    if (type === "end" && endPlace?.formatted_address) {
      setDestinationLocation(endPlace.formatted_address);
      if (endPlace.geometry?.location) {
        setDestinationCoordinates(latLngToString(endPlace.geometry.location));
      }
    }

    // Calculate route if both places have geometry
    if (startPlace?.geometry?.location && endPlace?.geometry?.location) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: startPlace.geometry.location,
          destination: endPlace.geometry.location,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            setDirectionsResponse(result);
            const leg = result.routes[0].legs[0];
            const distanceText = leg.distance?.text || "";
            const distanceValue = parseFloat(
              distanceText.replace(/[^\d.]/g, "")
            );
            const isKm = distanceText.includes("km");
            const baseMiles = isKm ? distanceValue * 0.621371 : distanceValue;
            const finalMiles = route_trip ? baseMiles * 2 : baseMiles;

            setMiles(finalMiles.toFixed(2));
          }
        }
      );
    }
  };

  // Round trip toggle
  const handleRoundTripChange = (checked: boolean) => {
    setRouteTrip(checked);
    if (miles) {
      const base = parseFloat(miles);
      const newMiles = checked ? base * 2 : base / 2;
      setMiles(newMiles.toFixed(2));
    }
  };

  // everyday toggle
  const handleEverydayToggle = (checked: boolean) => {
    if (checked) {
      setSelectedDays([...recurringDays]);
    } else {
      setSelectedDays([]);
    }
  };

  // individual day toggle
  const handleDayToggle = (day: string) => {
    if (selectedDays.length === recurringDays.length) {
      setSelectedDays([day.toLowerCase()]);
      return;
    }

    if (selectedDays.includes(day.toLowerCase())) {
      setSelectedDays(selectedDays.filter((d) => d !== day.toLowerCase()));
    } else {
      setSelectedDays([...selectedDays, day.toLowerCase()]);
    }
  };

  // Handle delete confirmation
  const handleDeleteImage = () => {
    if (selectedImageUuid) {
      setExistingImages((prev) =>
        prev.filter((image) => image.uuid !== selectedImageUuid)
      );
      setSelectedImageUuid(null);
      if (files.length + existingImages.length - 1 <= MAX_FILES) {
        setUploadError("");
      }
    }
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (uuid: string) => {
    setSelectedImageUuid(uuid);
    setShowDeleteModal(true);
  };

  // Handle closing delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedImageUuid(null);
  };

  // Handle successful deletion
  const handleDeleteSuccess = () => {
    handleDeleteImage();
    setShowDeleteModal(false);
  };

  // Submit handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation for recurring trips
    if (isRecurring && selectedDays.length === 0) {
      setRecurringError("Please select at least one day for recurring trips.");
      return;
    } else {
      setRecurringError("");
    }
    const daysToSend =
      isRecurring && selectedDays.length === recurringDays?.length
        ? "everyday"
        : selectedDays.join(", ");

    const entry: MileageEntry = {
      startingLocation,
      destinationLocation,
      source_coordinates: sourceCoordinates,
      destination_coordinates: destinationCoordinates,
      route_trip,
      miles,
      isRecurring,
      days: isRecurring ? daysToSend : "",
      photos: files,
      receipts: existingImages,
      uuid: existingMileageEntry || "",
    };

    onSave(entry); // pass data back to LogHoursAdd
  };

  const handleFileSelect = (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" is larger than 10 MB`);
        return false;
      }
      // if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      //   toast.error(`"${file.name}" is not an image or PDF`);
      //   return false;
      // }
      return true;
    });

    if (validFiles.length === 0) return;

    const currentSize = files.reduce((acc, f) => acc + f.size, 0);
    const newSize = validFiles.reduce((acc, f) => acc + f.size, 0);
    const totalFutureSize = currentSize + newSize;
    const totalFutureCount = files.length + existingImages.length + validFiles.length;

    if (totalFutureCount > MAX_FILES) {
      setUploadError(`You can only upload up to 5 files total`);
      toast.error("Maximum 5 files allowed");
      return;
    }

    if (totalFutureSize > MAX_TOTAL_SIZE) {
      setUploadError("Total file size cannot exceed 50 MB");
      toast.error("Total upload size exceeds 50 MB");
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
    setUploadError("");
    toast.success(`${validFiles.length} file(s) added`);
  };

  return (
    <Modal show={show} onHide={onHide} centered className="mileage-entry-modal">
      <div className="modal-scroller">
        <Modal.Header closeButton className="Mileage-Entry-header">
          <Modal.Title>Mileage Entry</Modal.Title>
        </Modal.Header>

        <Modal.Body className="Mileage-Entry-Body">
          <form onSubmit={handleSubmit}>
            {/* Starting Location */}
            <div className="mb-2">
              <div className="input-group Mileage-modal position-relative starting-location">
                <LocationIcon />
                {/* <input
                  type="text"
                  className="form-control commonInput bg-light"
                  placeholder="Enter your Starting Location"
                  value={startingLocation}
                  onChange={(e) => setStartingLocation(e.target.value)}
                  required
                /> */}

                {isLoaded ? (
                  <Autocomplete
                    onLoad={setStartAutocomplete}
                    onPlaceChanged={() => handlePlaceChange("start")}
                  >
                    <input
                      type="text"
                      className="form-control commonInput bg-light "
                      placeholder="Enter your Starting Location"
                      value={startingLocation}
                      onChange={(e) => setStartingLocation(e.target.value)}
                      required
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    className="form-control commonInput bg-light"
                    placeholder="Loading Google Places..."
                    disabled
                  />
                )}
              </div>
            </div>

            {/* Destination Location */}
            <div className="mb-2">
              <div className="input-group Mileage-modal position-relative destination-location">
                <LocationIcon />
                {/* <input
                  type="text"
                  className="form-control commonInput bg-light"
                  placeholder=" Enter your Destination Location"
                  value={destinationLocation}
                  onChange={(e) => setDestinationLocation(e.target.value)}
                  required
                /> */}

                {isLoaded ? (
                  <Autocomplete
                    onLoad={setEndAutocomplete}
                    onPlaceChanged={() => handlePlaceChange("end")}
                  >
                    <input
                      type="text"
                      className="form-control commonInput bg-light"
                      placeholder="Enter your Destination Location"
                      value={destinationLocation}
                      onChange={(e) => setDestinationLocation(e.target.value)}
                      required
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    className="form-control commonInput bg-light"
                    placeholder="Loading Google Places..."
                    disabled
                  />
                )}
              </div>
            </div>

            {/* Round Trip */}
            <div className="mb-2">
              <div className="input-group Mileage-modal position-relative">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="termsCheck"
                  checked={route_trip}
                  onChange={(e) => handleRoundTripChange(e.target.checked)}
                />
                <label className="form-check-label ms-3" htmlFor="termsCheck">
                  Round Trip
                </label>
              </div>
            </div>

            {/* Miles */}
            <div className="mb-2">
              <div className="input-group Mileage-modal position-relative">
                <TimerGreyIcon />
                <input
                  type="text"
                  className="form-control commonInput bg-light"
                  placeholder="Calculated miles"
                  value={miles ? `${miles} miles` : ""}
                  readOnly
                />
              </div>
            </div>

            {/* Recurring Trips Section */}
            <div className="row g-3 mb-2">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 ">
                <div className="recurring-trips bg-light rounded  mb-2 form-control">
                  <div className="d-flex  gap-3  align-content-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="recurringTrips"
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                    />
                    <label
                      className="form-check-label fw-bold"
                      htmlFor="recurringTrips"
                    >
                      Recurring Trips
                    </label>
                  </div>

                  {isRecurring && (
                    <>
                      <div className="recurring-trips-days">
                        {/* Everyday Checkbox */}
                        <div className="d-flex form-check mb-3 mt-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="everyday"
                            checked={
                              selectedDays.length === recurringDays?.length
                            }
                            onChange={(e) =>
                              handleEverydayToggle(e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="everyday"
                          >
                            Everyday
                          </label>
                        </div>

                        {/* Days of the Week */}
                        <div className="d-flex gap-2 flex-wrap week-days">
                          {recurringDays?.map((day, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleDayToggle(day)}
                              className={`btn rounded-circle ${selectedDays.includes(day.toLowerCase())
                                ? "btn-primary text-white"
                                : "btn-outline-primary"
                                }`}
                              style={{
                                width: "40px",
                                height: "40px",
                                padding: 0,
                              }}
                              disabled={
                                selectedDays.length === recurringDays?.length
                              }
                            >
                              {day[0].toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Inline error message */}
                      {recurringError && (
                        <div className="text-danger mt-2">{recurringError}</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-2">
              <div className="Upload-Photo d-flex align-items-center gap-3 mb-2">
                <UploadIcon />
                <h4>Upload Supporting Documents</h4>
              </div>

              <div className="mb-2 upload-section">
                <div
                  className={`upload-area rounded text-center p-3 bg-white ${isDragOver ? "drag-over" : ""}`}
                  style={{
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    borderColor: isDragOver ? "#0d6efd" : "#dee2e6",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    document.getElementById("fileUploadInput")?.click()
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = "copy";
                    setIsDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(false);
                    if (e.dataTransfer.files?.length) {
                      handleFileSelect(Array.from(e.dataTransfer.files));
                    }
                  }}
                >
                  <FileUploadWhiteIcon />
                  <p className="mb-0 text-muted">
                    Click or drag file to this area to upload
                  </p>

                  <input
                    type="file"
                    id="fileUploadInput"
                    hidden
                    multiple
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handleFileSelect(Array.from(e.target.files));
                        e.target.value = ""; // Reset input
                      }
                    }}
                  />
                </div>

                {/* Inline error */}
                {uploadError && (
                  <div className="text-danger mt-2">{uploadError}</div>
                )}

                {/* File Preview */}
                {(files.length > 0 || existingImages.length > 0) && (
                  <div className="mt-4 uploaded-files-section">

                    <div className="mb-3 p-2 bg-light rounded border">
                      <small className="text-muted fw-semibold">
                        Uploaded: {files.length + existingImages.length} / {MAX_FILES} files |
                        Size: {(files.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB / 50 MB
                      </small>
                      {files.reduce((acc, f) => acc + f.size, 0) > MAX_TOTAL_SIZE * 0.9 && (
                        <span className="text-warning ms-2">Warning: Near limit!</span>
                      )}
                    </div>

                    <div className="mt-3 text-start">
                      {existingImages.map((image, idx) => (
                        <div
                          key={image?.uuid}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <a
                            href={image?.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {image?.file_url.split("/").pop()}
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger cross-btn"
                            onClick={() => handleOpenDeleteModal(image.uuid)}
                          >
                            <i className="fas fa-times cross-btn"></i>
                          </button>
                        </div>
                      ))}

                      {files.map((file, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span>{file?.name}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger cross-btn"
                            onClick={() => {
                              setFiles((prev) => {
                                const updated = prev.filter((_, i) => i !== idx);
                                if (
                                  updated?.length + existingImages?.length <=
                                  MAX_FILES
                                ) {
                                  setUploadError("");
                                }
                                return updated;
                              });
                            }}
                          >
                            <i className="fas fa-times cross-btn"></i>
                          </button>
                        </div>
                      ))}
                    </div>

                  </div>
                )}
              </div>
            </div>

            <div className="modal-submit-button">
              <button type="submit" className="btn btn-warning">
                Save
              </button>
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer></Modal.Footer>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type="deleteMileageReceipt"
        uuid={selectedImageUuid}
        data={data}
        onSuccess={handleDeleteSuccess}
      />
    </Modal>
  );
};

export default MileageEntryModal;
